import { useId, useMemo, useState } from 'react'
import { cn } from '@/lib/cn'
import { MAX_GALLERY_ITEMS, mediaGalleryItem, parseGallery } from '@/lib/gallery'
import { parseVideo, VIDEO_LINK_ERROR } from '@/lib/video'
import { MediaPickerOverlay } from '../MediaPickerOverlay'

interface VideoFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
}

/** A YouTube/Vimeo link input that says when the link won't work. */
export function VideoField({ label, value, onChange, hint }: VideoFieldProps) {
  const id = useId()
  const [touched, setTouched] = useState(false)
  const invalid = value.trim() !== '' && parseVideo(value) === null
  const showError = touched && invalid

  return (
    <div className="field-row">
      <div className="field">
        <label className="field-name" htmlFor={id}>
          {label}
        </label>
        <div className={cn('field-box', showError && 'has-error')}>
          <input
            id={id}
            className="field-box-input text-center"
            type="url"
            inputMode="url"
            placeholder="https://www.youtube.com/watch?v=…"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={showError || undefined}
            aria-describedby={`${id}-help`}
          />
        </div>
        <div id={`${id}-help`} className={showError ? 'field-error' : 'field-hint'}>
          {showError ? VIDEO_LINK_ERROR : (hint ?? 'A YouTube or Vimeo link. Optional.')}
        </div>
      </div>
    </div>
  )
}

interface GalleryEditorProps {
  items: string[]
  onChange: (items: string[]) => void
  hint?: string
}

/**
 * Build a gallery: add images from your library or video links, reorder them
 * and remove them. The order here is the order viewers see.
 */
export function GalleryEditor({ items, onChange, hint }: GalleryEditorProps) {
  const [picking, setPicking] = useState(false)
  const [videoDraft, setVideoDraft] = useState('')
  const [videoError, setVideoError] = useState<string | null>(null)
  const parsed = useMemo(() => parseGallery(items), [items])
  const full = items.length >= MAX_GALLERY_ITEMS

  const add = (item: string) => {
    if (items.includes(item) || full) return
    onChange([...items, item])
  }

  const addVideo = () => {
    const video = parseVideo(videoDraft)
    if (!video) {
      setVideoError(VIDEO_LINK_ERROR)
      return
    }
    if (items.includes(video.url)) {
      setVideoError('That video is already in the gallery.')
      return
    }
    add(video.url)
    setVideoDraft('')
    setVideoError(null)
  }

  const move = (from: number, step: number) => {
    const to = from + step
    if (to < 0 || to >= items.length) return
    const next = [...items]
    ;[next[from], next[to]] = [next[to], next[from]]
    onChange(next)
  }

  return (
    <div className="field-row">
      <div className="field gallery-editor">
        <div className="field-name">Gallery (optional)</div>

        {picking ? (
          <MediaPickerOverlay
            onClose={() => setPicking(false)}
            onSelect={(media) => {
              if (media?.id) add(mediaGalleryItem(media.id))
              setPicking(false)
            }}
          />
        ) : null}

        {parsed.length > 0 ? (
          <ol className="gallery-editor-list">
            {parsed.map((item, index) => {
              const label = item.kind === 'video' ? `Video ${index + 1}` : `Image ${index + 1}`
              const thumb = item.kind === 'image' ? item.thumb : item.video.thumbnail
              return (
                <li className="gallery-editor-item" key={item.key}>
                  <div className="gallery-editor-thumb">
                    {thumb ? <img src={thumb} alt="" loading="lazy" /> : <span>Vimeo</span>}
                    {item.kind === 'video' ? (
                      <span className="gallery-editor-badge">Video</span>
                    ) : null}
                  </div>
                  <div className="gallery-editor-controls">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move ${label} earlier`}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === parsed.length - 1}
                      aria-label={`Move ${label} later`}
                    >
                      ›
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => onChange(items.filter((entry) => entry !== item.key))}
                      aria-label={`Remove ${label}`}
                    >
                      ×
                    </button>
                  </div>
                </li>
              )
            })}
          </ol>
        ) : null}

        <div className="gallery-editor-add">
          <button
            type="button"
            className="media-action"
            onClick={() => setPicking(true)}
            disabled={full}
          >
            Add image
          </button>
          <div className={cn('field-box gallery-editor-video', videoError && 'has-error')}>
            <input
              className="field-box-input"
              type="url"
              inputMode="url"
              placeholder="Add a YouTube or Vimeo link"
              aria-label="Video link to add to the gallery"
              value={videoDraft}
              disabled={full}
              onChange={(event) => {
                setVideoDraft(event.target.value)
                setVideoError(null)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addVideo()
                }
              }}
            />
          </div>
          <button
            type="button"
            className="media-action"
            onClick={addVideo}
            disabled={full || videoDraft.trim() === ''}
          >
            Add video
          </button>
        </div>
        <div
          className={videoError ? 'field-error' : 'field-hint'}
          role={videoError ? 'alert' : undefined}
        >
          {videoError ??
            (full
              ? `The gallery is full (${MAX_GALLERY_ITEMS} items).`
              : (hint ?? `Up to ${MAX_GALLERY_ITEMS} images and videos, shown in this order.`))}
        </div>
      </div>
    </div>
  )
}
